<template>
    <div>
        <div class="form">
            <div class="titleStep">
                <h1>Elige tu suscripción</h1>
                <p v-if="formData.product_type === 'fresh'">Plan personalizado para {{formData.petname}} ({{formData.weight}}kg)</p>
                <p v-else>Mientras más meses, más económica cada caja</p>
            </div>

            <div class="payment-type-selector mb-6">
                <div class="columns">
                    <div class="column is-6">
                        <b-radio v-model="form.payMethod"
                            name="payment-type"
                            native-value="subscription">
                            Suscripción
                        </b-radio>
                    </div>
                    <div class="column is-6">
                        <b-radio v-model="form.payMethod"
                            name="payment-type"
                            native-value="unique">
                            Pago único
                        </b-radio>
                    </div>
                </div>
            </div>
            
            <div class="spacer" style="height: 2rem;"></div>

            <!-- Planes FRESH (basados en cálculo inteligente) -->
            <div v-if="formData.product_type === 'fresh'" style="margin-bottom: 2rem;">
                <!-- Recomendación calculada -->
                <div class="fresh-recommendation">
                    <h3>📊 Plan recomendado para {{formData.petname}}</h3>
                    <div class="recommendation-factors">
                        <p><strong>Basado en:</strong></p>
                        <ul>
                            <li>Peso: {{formData.weight}} kg</li>
                            <li>Edad: {{getAgeDescription()}}</li>
                            <li>Actividad: {{formData.activityLevel}}</li>
                        </ul>
                    </div>
                    
                    <div class="recommendation-result">
                        <p class="recommendation-amount">
                            Recomendamos <strong>{{recommendedPortions}} envases/mes</strong>
                        </p>
                        <p class="recommendation-detail">
                            ({{dailyGrams}}g/día × 30 días = {{monthlyGrams/1000}}kg/mes)
                        </p>
                    </div>

                    <!-- Ajuste manual -->
                    <div class="portion-adjuster">
                        <label>Ajustar cantidad si es necesario:</label>
                        <div class="adjuster-controls">
                            <button type="button" @click="adjustPortions(-2)" class="btn-adjust">−</button>
                            <span class="portion-display">{{adjustedPortions}} envases</span>
                            <button type="button" @click="adjustPortions(2)" class="btn-adjust">+</button>
                        </div>
                    </div>

                    <!-- Mensaje tranquilizador -->
                    <div class="reassurance-message">
                        <p>ℹ️ Ajustaremos la cantidad exacta antes de preparar tu pedido según las necesidades específicas de {{formData.petname}}</p>
                    </div>

                    <!-- Explicación colapsable -->
                    <div class="calculation-info">
                        <a @click="showCalculationDetails = !showCalculationDetails" style="cursor: pointer; color: #666; font-size: 0.9rem;">
                            {{ showCalculationDetails ? '▼' : '▶' }} ¿Cómo calculamos esto?
                        </a>
                        <div v-if="showCalculationDetails" class="calculation-details">
                            <p>Usamos la fórmula veterinaria RER (Requerimiento Energético en Reposo) ajustada por edad y actividad:</p>
                            <ul>
                                <li>RER = 70 × (peso<sup>0.75</sup>) = {{rer}} kcal/día</li>
                                <li>Multiplicador {{getDERMultiplierText()}}: ×{{derMultiplier}}</li>
                                <li>Energía diaria necesaria: {{dailyCalories}} kcal/día</li>
                                <li>Comida húmeda premium: ~122 kcal/100g</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <!-- Frecuencia de entrega -->
                <div class="delivery-frequency-selector">
                    <h3>🚚 Frecuencia de entrega</h3>
                    <div class="columns is-mobile">
                        <div class="column is-6">
                            <div class="frequency-option" 
                                :class="{ 'active': form.deliveryFrequency === '1x' }"
                                @click="form.deliveryFrequency = '1x'; updatePriceWithFrequency()">
                                <div class="frequency-header">
                                    <b-radio v-model="form.deliveryFrequency" 
                                        native-value="1x"
                                        @input="updatePriceWithFrequency">
                                        1 entrega mensual
                                    </b-radio>
                                </div>
                                <p class="frequency-detail">Todo el pedido en una sola entrega</p>
                                <p class="frequency-price free">Despacho incluido</p>
                            </div>
                        </div>
                        <div class="column is-6">
                            <div class="frequency-option" 
                                :class="{ 'active': form.deliveryFrequency === '2x' }"
                                @click="form.deliveryFrequency = '2x'; updatePriceWithFrequency()">
                                <div class="frequency-header">
                                    <b-radio v-model="form.deliveryFrequency" 
                                        native-value="2x"
                                        @input="updatePriceWithFrequency">
                                        2 entregas quincenales
                                    </b-radio>
                                </div>
                                <p class="frequency-detail">Pedido dividido cada 2 semanas (más fresco)</p>
                                <p class="frequency-price">+ $2.990/mes</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Planes FRESH (basados en cantidad ajustada) -->
            <div class="card-selects prices" v-if="formData.product_type === 'fresh'">
                <div class="columns is-multiline">
                    <div class="column is-4" v-for="plan in availableFreshPlansCalculated" :key="plan.id">
                        <b-radio v-model="form.plan"
                            @input="setPrice"
                            name="name"
                            :native-value="plan.id + '_x1'"
                            @change.native="nextStep"
                            :class="[form.plan === plan.id + '_x1' ? 'active' : '']">
                            <img src="@/assets/x1.png">
                            <div>
                                <h3>1 mes</h3>
                                <span>{{freshPrices[plan.id + '_x1'] | currency}}</span>
                                <small>{{plan.portions}} envases/mes</small>
                                <small class="price-per-unit">{{pricePerUnitByPlan[plan.id + '_x1'] | currency}}/envase</small>
                            </div>
                        </b-radio>
                    </div>
                    <div class="column is-4" v-for="plan in availableFreshPlansCalculated" :key="plan.id + '_3'">
                        <b-radio v-model="form.plan"
                            @input="setPrice"
                            name="name"
                            :native-value="plan.id + '_x3'"
                            @change.native="nextStep"
                            :class="[form.plan === plan.id + '_x3' ? 'active' : '']">
                            <img src="@/assets/x3.png">
                            <div>
                                <h3>3 meses</h3>
                                <span>{{freshPrices[plan.id + '_x3'] | currency}}</span>
                                <small>{{plan.portions}} envases/mes</small>
                                <small class="discount-badge">{{pricePerUnitByPlan[plan.id + '_x3'] | currency}}/envase (-5%)</small>
                            </div>
                        </b-radio>
                    </div>
                    <div class="column is-4" v-for="plan in availableFreshPlansCalculated" :key="plan.id + '_6'">
                        <b-radio v-model="form.plan"
                            @input="setPrice"
                            name="name"
                            :native-value="plan.id + '_x6'"
                            @change.native="nextStep"
                            :class="[form.plan === plan.id + '_x6' ? 'active' : '']">
                            <div class="ribbon ribbon-top-right"><span>Recomendado</span></div>
                            <img src="@/assets/x6.png">
                            <div>
                                <h3>6 meses</h3>
                                <span>{{freshPrices[plan.id + '_x6'] | currency}}</span>
                                <small>{{plan.portions}} envases/mes</small>
                                <small class="discount-badge">{{pricePerUnitByPlan[plan.id + '_x6'] | currency}}/envase (-10%)</small>
                            </div>
                        </b-radio>
                    </div>
                    <div class="column is-12 v-column" v-if="$v.form.plan.$error">
                        <p class="help is-danger">Plan inválido</p>
                    </div>
                </div>
            </div>

            <!-- Planes TOYS (original) -->
            <div class="card-selects prices" v-else>
                <!-- Light box -->
                <div class="columns is-multiline" v-if="formData.boxSize === 'Light'">
                    <div class="column is-4">
                        <b-radio v-model="form.plan"
                            @input="setPrice"
                            name="name"
                            native-value="light_x1"
                            @change.native="nextStep"
                            :class="[form.plan === 'light_x1' ? 'active' : '']">
                            <img src="@/assets/x1.png">
                            <div>
                                <h3>1 mes</h3>
                                <span>{{prices.light_x1 | currency}} / caja</span>
                            </div>
                        </b-radio>
                    </div>
                    <div class="column is-4">
                        <b-radio v-model="form.plan"
                            @input="setPrice"
                            name="name"
                            native-value="light_x3"
                            @change.native="nextStep"
                            :class="[form.plan === 'light_x3' ? 'active' : '']">
                            <img src="@/assets/x3.png">
                            <div>
                                <h3>3 meses</h3>
                                <span>{{prices.light_x3 | currency}} / caja</span>
                            </div>
                        </b-radio>
                    </div>
                    <div class="column is-4">
                        <b-radio v-model="form.plan"
                            @input="setPrice"
                            name="name"
                            native-value="light_x6"
                            @change.native="nextStep"
                            :class="[form.plan === 'light_x6' ? 'active' : '']">
                            <div class="ribbon ribbon-top-right"><span>Recomendado</span></div>
                            <img src="@/assets/x6.png">
                            <div>
                                <h3>6 meses</h3>
                                <span>{{prices.light_x6 | currency}} / caja</span>
                            </div>
                        </b-radio>
                    </div>
                    <div class="column is-12 v-column" v-if="$v.form.plan.$error">
                        <p class="help is-danger">Plan inválido</p>
                    </div>
                </div>
                
                <!-- Normal box -->
                <div class="columns is-multiline">
                    <div class="column is-4">
                        <b-radio v-model="form.plan"
                            @input="setPrice"
                            name="name"
                            native-value="normal_x1"
                            @change.native="nextStep"
                            :class="[form.plan === 'normal_x1' ? 'active' : '']">
                            <img src="@/assets/x1.png">
                            <div>
                                <h3>1 mes</h3>
                                <span>{{prices.normal_x1 | currency}} / caja</span>
                            </div>
                        </b-radio>
                    </div>
                    <div class="column is-4">
                        <b-radio v-model="form.plan"
                            @input="setPrice"
                            name="name"
                            native-value="normal_x3"
                            @change.native="nextStep"
                            :class="[form.plan === 'normal_x3' ? 'active' : '']">
                            <img src="@/assets/x3.png">
                            <div>
                                <h3>3 meses</h3>
                                <span>{{prices.normal_x3 | currency}} / caja</span>
                            </div>
                        </b-radio>
                    </div>
                    <div class="column is-4">
                        <b-radio v-model="form.plan"
                            @input="setPrice"
                            name="name"
                            native-value="normal_x6"
                            @change.native="nextStep"
                            :class="[form.plan === 'normal_x6' ? 'active' : '']">
                            <div class="ribbon ribbon-top-right"><span>Recomendado</span></div>
                            <img src="@/assets/x6.png">
                            <div>
                                <h3>6 meses</h3>
                                <span>{{prices.normal_x6 | currency}} / caja</span>
                            </div>
                        </b-radio>
                    </div>
                    <div class="column is-12 v-column" v-if="$v.form.plan.$error">
                        <p class="help is-danger">Plan inválido</p>
                    </div>
                </div>
            </div>

            <a class="backStep" @click="backStep">Volver</a>
            <div class="disclaimer">
                <h6><b>Si {{formData.petname}} no está 100% satisfecho con su TupiBox, trabajaremos contigo en hacer la mejor opción</b></h6>
                <p v-if="formData.product_type === 'fresh'">
                    <small>
                        <span v-if="form.deliveryFrequency === '1x'">Despacho gratuito en todo Santiago de Chile. Tu pedido completo llega en una sola entrega mensual.</span>
                        <span v-else>Tu pedido se divide en 2 entregas quincenales (la primera con despacho gratuito en Santiago, la segunda +$2.990).</span>
                        Para regiones tiene un recargo por método de envío. Te contactaremos para que renueves tu plan, pero puedes decidir no seguir con TupiBox cuando quieras.
                    </small>
                </p>
                <p v-else>
                    <small>Despacho gratuito en todo Santiago de Chile, para regiones tiene un recargo por método de envío. Te contactaremos para que renueves tu plan, pero puedes decidir no seguir con TupiBox cuando quieras.</small>
                </p>
            </div>
        </div>
    </div>
</template>

<script>
    import {validationMixin} from 'vuelidate'
    import {required} from 'vuelidate/lib/validators'
    import StepMixin from '../../mixins/StepMixin'
    import crypto from 'crypto'

    export default {
        props: ['clickedNext', 'currentStep'],
        mixins: [validationMixin, StepMixin],
        data() {
            return {
                form: {
                    payMethod: 'subscription',
                    plan: '',
                    price: null,
                    originalPrice: null,
                    deliveryFrequency: '1x' // '1x' (1 vez/mes) o '2x' (2 veces/mes)
                },
                showCalculationDetails: false,
                adjustedPortions: 0,
            }
        },
        mounted() {
            // Calcular porciones recomendadas al montar el componente
            console.log('🏗️ [StepSix] Component mounted, product_type:', this.formData.product_type);
            console.log('🏗️ [StepSix] formData:', JSON.stringify(this.formData, null, 2));
            
            if (this.formData.product_type === 'fresh') {
                // Usar múltiples $nextTick para asegurar que Vue termine de renderizar
                this.$nextTick(() => {
                    this.$nextTick(() => {
                        const calculated = this.recommendedPortions;
                        console.log('📊 [StepSix mounted] Calculated portions:', calculated);
                        console.log('📊 [StepSix mounted] Weight:', this.formData.weight, 'Age:', this.formData.ageInMonths, 'Activity:', this.formData.activityLevel);
                        console.log('📊 [StepSix mounted] RER:', this.rer, 'DER Multiplier:', this.derMultiplier, 'Daily Calories:', this.dailyCalories);
                        
                        if (calculated > 0) {
                            this.adjustedPortions = calculated;
                            console.log('✅ [StepSix] adjustedPortions set to:', this.adjustedPortions);
                        } else {
                            // Si aún no hay cálculo, usar default
                            console.warn('⚠️ [StepSix] No se pudo calcular porciones (faltan datos)');
                            this.adjustedPortions = 28; // Default razonable
                        }
                        
                        // Forzar re-render
                        this.$forceUpdate();
                    });
                });
            }
        },
        watch: {
            // Actualizar porciones ajustadas si cambia la recomendación
            recommendedPortions(newVal) {
                if (this.adjustedPortions === 0) {
                    this.adjustedPortions = newVal;
                }
            },
            // Recalcular precios cuando cambien las porciones ajustadas
            adjustedPortions(newVal) {
                console.log('📦 [StepSix] adjustedPortions changed to:', newVal);
                // Si hay un plan seleccionado, recalcular precio
                if (this.form.plan) {
                    this.setPrice(this.form.plan);
                }
            }
        },
        computed: {
            formData(){
                return this.$parent.$parent.$parent.formData
            },
            prices(){
                return this.$parent.$parent.$parent.configs.prices
            },
            // Cálculos de energía basados en estándares veterinarios
            rer() {
                const weight = this.formData.weight || 10;
                return Math.round(70 * Math.pow(weight, 0.75));
            },
            derMultiplier() {
                const ageInMonths = this.formData.ageInMonths || 24;
                const activity = this.formData.activityLevel || 'Medio';
                
                // Multiplicador base por actividad
                let activityMult = 1.4;
                if (activity === 'Bajo') activityMult = 1.2;
                if (activity === 'Alto') activityMult = 1.6;
                
                // Multiplicador por edad
                let ageMult = 1.0;
                if (ageInMonths < 4) ageMult = 3.0;        // Cachorro muy joven
                else if (ageInMonths < 12) ageMult = 2.0;  // Cachorro
                else if (ageInMonths < 24) ageMult = 1.2;  // Adulto joven
                else if (ageInMonths >= 84) ageMult = 0.8; // Senior (7+ años)
                
                return activityMult * ageMult;
            },
            dailyCalories() {
                return Math.round(this.rer * this.derMultiplier);
            },
            dailyGrams() {
                // 122 kcal por 100g (promedio comida húmeda premium)
                return Math.round((this.dailyCalories / 122) * 100);
            },
            monthlyGrams() {
                return this.dailyGrams * 30;
            },
            recommendedPortions() {
                // Envases de 500g
                return Math.ceil(this.monthlyGrams / 500);
            },
            availableFreshPlansCalculated(){
                // Un solo plan basado en el cálculo
                const portions = this.adjustedPortions || this.recommendedPortions;
                return [{ 
                    id: 'fresh_calculated', 
                    name: 'Personalizado', 
                    portions: portions,
                    pricePerUnit: 5490 // $5.490 por envase de 500g (comida)
                }];
            },
            // Precios calculados dinámicamente para Fresh
            freshPrices() {
                const pricePerUnit = 5490; // Precio base por envase (1 mes)
                const portions = this.adjustedPortions || this.recommendedPortions;
                const deliveryCharge = this.form.deliveryFrequency === '2x' ? 2990 : 0;
                
                // Función para redondear hacia arriba terminando en 90
                const roundTo90 = (price) => {
                    const hundreds = Math.ceil(price / 100);
                    return (hundreds * 100) - 10; // Termina en 90
                };
                
                // Precios con descuentos + cargo de entrega, luego redondeados
                const base1Month = (pricePerUnit * portions) + deliveryCharge;
                const base3Months = (Math.round(pricePerUnit * 0.95) * portions) + deliveryCharge; // 5% descuento
                const base6Months = (Math.round(pricePerUnit * 0.90) * portions) + deliveryCharge; // 10% descuento
                
                return {
                    'fresh_calculated_x1': roundTo90(base1Month),
                    'fresh_calculated_x3': roundTo90(base3Months),
                    'fresh_calculated_x6': roundTo90(base6Months)
                };
            },
            // Precios por envase después de descuento (para mostrar en UI)
            // Calculado a partir del total redondeado dividido por cantidad de envases
            pricePerUnitByPlan() {
                const portions = this.adjustedPortions || this.recommendedPortions;
                if (portions === 0) return {};
                
                return {
                    'fresh_calculated_x1': Math.round(this.freshPrices['fresh_calculated_x1'] / portions),
                    'fresh_calculated_x3': Math.round(this.freshPrices['fresh_calculated_x3'] / portions),
                    'fresh_calculated_x6': Math.round(this.freshPrices['fresh_calculated_x6'] / portions)
                };
            },
            availableFreshPlans(){
                // Mantener para compatibilidad (deprecated)
                return this.availableFreshPlansCalculated;
            }
        },
        validations: {
            form: {
                plan: { required },
            }
        },
        methods: {
            getAgeDescription() {
                const months = this.formData.ageInMonths || 24;
                const years = Math.floor(months / 12);
                const remainingMonths = months % 12;
                
                if (months < 4) return `${months} meses (cachorro muy joven)`;
                if (months < 12) return `${months} meses (cachorro)`;
                if (months < 24) return `${years} año${years > 1 ? 's' : ''} ${remainingMonths} meses (adulto joven)`;
                if (months < 84) return `${years} año${years > 1 ? 's' : ''} (adulto)`;
                return `${years} años (senior)`;
            },
            getDERMultiplierText() {
                const ageInMonths = this.formData.ageInMonths || 24;
                const activity = this.formData.activityLevel || 'Medio';
                
                if (ageInMonths < 4) return `cachorro muy joven + actividad ${activity.toLowerCase()}`;
                if (ageInMonths < 12) return `cachorro + actividad ${activity.toLowerCase()}`;
                if (ageInMonths < 24) return `adulto joven + actividad ${activity.toLowerCase()}`;
                if (ageInMonths >= 84) return `senior + actividad ${activity.toLowerCase()}`;
                return `adulto + actividad ${activity.toLowerCase()}`;
            },
            adjustPortions(delta) {
                const newValue = this.adjustedPortions + delta;
                // Mínimo absoluto: 9 envases
                // Máximo: 150% de recomendación
                const min = 9;
                const max = Math.ceil(this.recommendedPortions * 1.5);
                
                if (newValue >= min && newValue <= max) {
                    this.adjustedPortions = newValue;
                }
            },
            updatePriceWithFrequency() {
                // Recalcular precio si hay un plan seleccionado
                if (this.form.plan) {
                    this.setPrice(this.form.plan);
                }
            },
            setPrice(plan){
                console.log('📦 [StepSix] Plan selected:', plan)
                let multiplier = plan.slice(-1)
                
                // Para Fresh, usar precios calculados dinámicamente
                if (this.formData.product_type === 'fresh') {
                    const freshPrice = this.freshPrices[plan];
                    if (freshPrice) {
                        // El precio ya incluye el cargo de entrega si corresponde
                        const finalPrice = freshPrice * parseInt(multiplier);
                        
                        this.form.price = finalPrice;
                        this.form.originalPrice = finalPrice;
                        console.log('📦 [StepSix] Fresh price calculated:', this.form.price);
                        console.log('📦 [StepSix] Delivery frequency:', this.form.deliveryFrequency);
                    }
                    
                    // Calcular porciones para Fresh
                    const basePlanId = plan.replace(/_x[136]$/, ''); // Remover multiplicador
                    const selectedPlan = this.availableFreshPlansCalculated.find(p => p.id === basePlanId);
                    if (selectedPlan) {
                        this.form.portions = selectedPlan.portions * parseInt(multiplier);
                        this.form.recommendedPortions = this.recommendedPortions;
                        this.form.adjustedPortions = this.adjustedPortions;
                        
                        console.log('📦 [StepSix] Portions calculated:', this.form.portions);
                        console.log('📦 [StepSix] Recommended:', this.recommendedPortions);
                        console.log('📦 [StepSix] Adjusted:', this.adjustedPortions);
                        console.log('📦 [StepSix] Base plan:', selectedPlan);
                        console.log('📦 [StepSix] Multiplier:', multiplier);
                    }
                } else {
                    // Para Toys, usar precios estáticos
                    this.form.price = this.prices[plan] * multiplier
                    this.form.originalPrice = this.prices[plan] * multiplier
                }
            }
        },
    }
</script>

<style scoped>
small {
    display: block;
    font-size: 0.85rem;
    color: #666;
    margin-top: 0.25rem;
}

small.discount-badge {
    color: #28a745;
    font-weight: 600;
    font-size: 0.75rem;
}

small.price-per-unit {
    color: #6c757d;
    font-size: 0.75rem;
}

.delivery-frequency-selector {
    background: #f8f9fa;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 2rem;
}

.delivery-frequency-selector h3 {
    margin-bottom: 1rem;
    font-size: 1.2rem;
    color: #2c3e50;
}

.frequency-option {
    background: white;
    border: 2px solid #dee2e6;
    border-radius: 8px;
    padding: 1rem;
    cursor: pointer;
    transition: all 0.2s;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.frequency-option:hover {
    border-color: #007bff;
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
}

.frequency-option.active {
    border-color: #007bff;
    background: #e7f3ff;
}

.frequency-header {
    margin-bottom: 0.5rem;
}

.frequency-detail {
    font-size: 0.85rem;
    color: #6c757d;
    margin: 0.5rem 0;
    flex-grow: 1;
}

.frequency-price {
    font-weight: 600;
    color: #dc3545;
    font-size: 0.9rem;
    margin: 0;
}

.frequency-price.free {
    color: #28a745;
}

.fresh-recommendation {
    background: #f8f9fa;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 2rem;
}

.fresh-recommendation h3 {
    margin-bottom: 1rem;
    font-size: 1.3rem;
    color: #2c3e50;
}

.recommendation-factors {
    margin-bottom: 1.5rem;
}

.recommendation-factors p {
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.recommendation-factors ul {
    list-style: none;
    padding-left: 0;
    margin-top: 0.5rem;
}

.recommendation-factors li {
    padding: 0.3rem 0;
    color: #495057;
}

.recommendation-result {
    background: white;
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 1.5rem;
    text-align: center;
}

.recommendation-amount {
    font-size: 1.4rem;
    margin-bottom: 0.5rem;
    color: #2c3e50;
}

.recommendation-amount strong {
    color: #28a745;
}

.recommendation-detail {
    font-size: 0.9rem;
    color: #6c757d;
    margin: 0;
}

.portion-adjuster {
    margin-bottom: 1.5rem;
}

.portion-adjuster label {
    display: block;
    margin-bottom: 0.75rem;
    font-weight: 500;
    color: #495057;
}

.adjuster-controls {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
}

.btn-adjust {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid #007bff;
    background: white;
    color: #007bff;
    font-size: 1.5rem;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-adjust:hover {
    background: #007bff;
    color: white;
}

.btn-adjust:active {
    transform: scale(0.95);
}

.portion-display {
    font-size: 1.3rem;
    font-weight: 600;
    min-width: 150px;
    text-align: center;
    color: #2c3e50;
}

.reassurance-message {
    background: #e7f3ff;
    border-left: 4px solid #007bff;
    padding: 1rem;
    border-radius: 4px;
    margin-bottom: 1rem;
}

.reassurance-message p {
    margin: 0;
    font-size: 0.9rem;
    color: #004085;
}

.calculation-info {
    border-top: 1px solid #dee2e6;
    padding-top: 1rem;
    margin-top: 1rem;
}

.calculation-details {
    margin-top: 1rem;
    padding: 1rem;
    background: white;
    border-radius: 6px;
    font-size: 0.9rem;
    color: #495057;
}

.calculation-details p {
    margin-bottom: 0.5rem;
}

.calculation-details ul {
    list-style: none;
    padding-left: 1rem;
    margin: 0;
}

.calculation-details li {
    padding: 0.25rem 0;
}
</style>
